import { useState } from 'react';
import type { BillingClass, BillingClassBillType } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { BillingClassFormModal } from './BillingClassFormModal.js';
import { BILL_TYPE_LABELS } from './billType.js';
import { useBillingClasses } from './api.js';

function billTypeTone(billType: BillingClassBillType): 'green' | 'amber' | 'gray' {
  switch (billType) {
    case 'bill':
      return 'green';
    case 'no_charge':
      return 'amber';
    default:
      return 'gray';
  }
}

export function BillingClassesPage(): JSX.Element {
  const { can } = useAuth();
  const canEdit = can('products', 'edit');

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BillingClass | null>(null);

  const query = useBillingClasses({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
  });

  const billingClasses = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const handleSearchSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (billingClass: BillingClass): void => {
    setEditing(billingClass);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Billing Classes"
        description="Control how logged hours are billed and shown on invoices."
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="billingClasses" className="h-4 w-4" />
              New billing class
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <form onSubmit={handleSearchSubmit} className="flex max-w-md items-center gap-2">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Billing</th>
              <th className="px-4 py-3 font-medium">On invoice</th>
              <th className="px-4 py-3 font-medium">Default</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={5}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={billingClasses.length === 0}
              emptyTitle="No billing classes found"
              emptyDescription={
                search
                  ? 'Try a different search term.'
                  : 'Create your first billing class to get started.'
              }
              emptyAction={
                canEdit && !search ? (
                  <Button onClick={openCreate}>New billing class</Button>
                ) : undefined
              }
            >
              {billingClasses.map((billingClass) => (
                <tr
                  key={billingClass.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(billingClass)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{billingClass.name}</td>
                  <td className="px-4 py-3">
                    <Badge tone={billTypeTone(billingClass.billType)}>
                      {BILL_TYPE_LABELS[billingClass.billType]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {billingClass.showOnInvoice ? 'Yes' : 'No'}
                  </td>
                  <td className="px-4 py-3">
                    {billingClass.isDefault ? <Badge tone="blue">Default</Badge> : null}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={billingClass.isActive ? 'green' : 'gray'}>
                      {billingClass.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && billingClasses.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <BillingClassFormModal
        isOpen={formOpen}
        billingClass={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
